import { BehaviorSubject, Observable, lastValueFrom, tap } from 'rxjs';
import { Injectable } from "@angular/core";
import { User } from "@models/user.model";
import { ApiResponse, ApiService } from "./api.service";
import { PaymentMethodService } from './payment-method.service';
import { ReservationService } from './reservation.service';
import { OrderService } from './order.service';
import { Router } from '@angular/router';
import { EventService } from './event.service';

export class ValidationErrorsLogin {
  email?: Array<string>;
  password?: Array<string>;
}

export class ValidationErrorsRegistration {
  // either explicitly define the list of validation keys you might get, or
  // make a list of keys which can be anything of type string...
  // and I'm not sure how to do that.
  first_name?: Array<string>;
  last_name?: Array<string>;
  email?: Array<string>;
  mobile?: Array<string>;
  date_of_birth?: Array<string>;
  gender?: Array<string>;
  country_residence?: Array<string>;
  password?: Array<string>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private localStorageKey = 'lgdin';
  private userCache = new BehaviorSubject<User | null>(null);
  private userLoggedInCache = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    private orderService: OrderService,
    private paymentMethodService: PaymentMethodService,
    private reservationService: ReservationService,
    private router: Router,
    private eventService: EventService
  ) { }

  setUserLoggedIn() {
    this.userLoggedInCache.next(localStorage.getItem(this.localStorageKey) === 'y');
  }

  get userLoggedIn() {
    return this.userLoggedInCache.asObservable();
  }

  get userLoggedInSnapshot() {
    this.setUserLoggedIn();
    return this.userLoggedInCache.value;
  }

  get user() {
    return this.userCache.asObservable();
  }

  get userSnapshot(): User | null {
    return this.userCache.value;
  }

  async setUser(user: User | null, redirectTo: string | null = null) {
    if (user?.orders) {
      this.orderService.setOrders(user?.orders);
      delete user.orders;
    }

    if (user?.payment_methods) {
      this.paymentMethodService.setMethods(user.payment_methods);
      delete user.payment_methods;
    }

    localStorage.setItem(this.localStorageKey, user ? 'y' : 'n');
    
    this.setUserLoggedIn();

    this.userCache.next(user);

    if (redirectTo) {
      if (redirectTo === 'checkout') {
        const event = this.eventService.eventSnapshot;
        redirectTo = '/' + event?.slug + '/tickets';

        const selectedTickets = sessionStorage.getItem('resAfLgn');

        if (selectedTickets && event) {
          try {
            const response = await lastValueFrom(this.reservationService.createOrUpdate(event.id, JSON.parse(selectedTickets)));
            redirectTo += response.data.add_ons_available ? '/add-ons' : '/checkout';
          } catch {
            //
          }
        }
      }

      this.router.navigateByUrl(redirectTo);
    }
  }

  getUser(): Observable<ApiResponse<User>> {
    return this.apiService.get<ApiResponse<User>>('me').pipe(tap({
      next: (response: any) => {
        this.setUser(response.data);
      }
    }));
  }

  login(params: any = {}, redirectTo: string | null = null): Observable<ApiResponse<User>> {
    return this.apiService.post<ApiResponse<User>>('login', params).pipe(tap({
      next: (response: any) => {
        this.setUser(response.data, redirectTo);
      }
    }));
  }

  logout(): Observable<ApiResponse<null>> {
    this.setUser(null);
    this.reservationService.setReservation(null);
    this.paymentMethodService.setMethods(undefined);
    this.orderService.setOrders(undefined);
    return this.apiService.post<ApiResponse<null>>('logout', {});
  }

  register(params: any = {}, redirectTo: string | null = null): Observable<ApiResponse<User>> {
    return this.apiService.post<ApiResponse<User>>('register', params).pipe(tap({
      next: (response: any) => {
        this.setUser(response.data, redirectTo);
      }
    }));
  }

  updateUser(id:number, params: any = {} , redirectTo: string | null = null): Observable<ApiResponse<User>> {
    return this.apiService.patch<ApiResponse<User>>(`users/${id}`, params).pipe(tap({
      next: (response: any) => {
        this.setUser(response.data, redirectTo);
      }
    }));
  }

  resetPassword(params: any): Observable<null> {
    return this.apiService.post<null>(`forgot-password`, params);
  }

  acceptPolicies(params: any): Observable<null> {
    return this.apiService.post<null>('user-policies', params);
  }
}
